import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import { IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceListVariationDto,
  GenericExperienceFe,
  GenericVariationFe,
  OnsiteInjectionDto,
} from "@intelligems/ig-types";
import { Grid, List, ListItem } from "@mui/material";
import {
  generateRolloutChangesCopy,
  getNonNativeOfferTestModsRollout,
  hasOnlyNativeMods,
  modLabelKey,
} from "@src/features/sharedExperience/components/rollouts/utils";
import { selectedAudience } from "@src/store/slices/audiences";
import { selectedOffers } from "@src/store/slices/offers";
import { selectedOnsiteEdits } from "@src/store/slices/onsiteEdits";
import { selectedInjections } from "@src/store/slices/onsiteInjections";
import { selectedPageTargeting } from "@src/store/slices/pageTargeting";
import { selectedRedirects } from "@src/store/slices/redirects";

interface RolloutsSummaryProps {
  selectedVariationId: string;
  experience: GenericExperienceFe;
  variations: (Omit<ExperienceListVariationDto, "experienceId"> & {
    experienceId?: string;
  })[];
  ignoreOtherChanges: boolean | null;
  isAllVisitors: string;
}
export const RolloutsSummary = ({
  selectedVariationId,
  variations,
  experience,
  ignoreOtherChanges,
  isAllVisitors,
}: RolloutsSummaryProps) => {
  const audience = useSelector(selectedAudience);
  const pageTargeting = useSelector(selectedPageTargeting);
  const redirects = useSelector(selectedRedirects);
  const offers = useSelector(selectedOffers);
  const onsiteEdits = useSelector(selectedOnsiteEdits);
  const onsiteInjections = useSelector(
    selectedInjections
  ) as OnsiteInjectionDto[];
  const modsToRollout = useMemo(() => {
    return getNonNativeOfferTestModsRollout(
      redirects,
      onsiteEdits,
      onsiteInjections,
      offers,
      selectedVariationId,
      Boolean(audience?.filters?.length > 0) || false
    );
  }, [experience.testTypes, audience?.filters]);

  const foundRedirectInfo = useMemo(() => {
    if (experience.testTypes.hasTestContentTheme) {
      return redirects.find(
        (redirect) => redirect.variationId === selectedVariationId
      )?.queryParams[0].value;
    }
    return null;
  }, [redirects, experience.testTypes, selectedVariationId]);

  return (
    <Grid container item direction={"column"}>
      <Grid sx={{ marginBottom: "20px" }} item>
        <IgTypography igStyle={"semibold"}>
          All set. Once you've reviewed the below, click to complete the
          rollout.
        </IgTypography>
      </Grid>
      <Grid item>
        <List
          disablePadding={true}
          dense={true}
          sx={{
            listStyleType: "disc",
            marginLeft: "24px",
          }}
        >
          <ListItem
            disableGutters={true}
            sx={{ display: "list-item", marginBottom: "10px" }}
          >
            <IgTypography>
              <IgTypography>
                {
                  variations.find(
                    (variation) => variation.id === selectedVariationId
                  )?.name
                }{" "}
              </IgTypography>
              will be rolled out
            </IgTypography>
          </ListItem>
          {experience.testTypes.hasTestContentTheme &&
            audience?.filters?.length === 0 && (
              <ListItem
                disableGutters={true}
                sx={{ display: "list-item", marginBottom: "10px" }}
              >
                <IgTypography>
                  <IgTypography igStyle={"semibold"}>
                    Theme {foundRedirectInfo}{" "}
                  </IgTypography>{" "}
                  will be applied as the live theme in Shopify
                </IgTypography>
              </ListItem>
            )}
          {modsToRollout.length ? (
            <ListItem
              disableGutters={true}
              sx={{ display: "list-item", marginBottom: "6px" }}
            >
              <IgTypography>
                The following changes will be{" "}
                {ignoreOtherChanges === true ? " not be " : ""}
                rolled out as an Experience:
              </IgTypography>
              <List sx={{ listStyleType: "disc", marginTop: "6px" }}>
                {modsToRollout.map((val) => (
                  <ListItem
                    key={val}
                    disableGutters={true}
                    sx={{ display: "list-item", marginLeft: "24px" }}
                  >
                    <IgTypography igStyle={"semibold"}>
                      {modLabelKey[val]}
                    </IgTypography>
                  </ListItem>
                ))}
              </List>
            </ListItem>
          ) : null}
          <ListItem
            sx={{ display: "list-item", marginBottom: "10px" }}
            disableGutters={true}
          >
            <IgTypography>
              {generateRolloutChangesCopy(
                audience,
                pageTargeting.length,
                isAllVisitors === "link"
              )}
            </IgTypography>
          </ListItem>
          {ignoreOtherChanges !== true &&
            !hasOnlyNativeMods(experience.testTypes) && (
              <ListItem
                sx={{ display: "list-item", marginBottom: "10px" }}
                disableGutters={true}
              >
                <IgTypography>
                  You’ll have a chance to further edit changes, audience, and
                  more before activating the experience
                </IgTypography>
              </ListItem>
            )}
        </List>
      </Grid>
    </Grid>
  );
};
